import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { userService, type UserProfile } from '@/services/userService';

interface UpdateProfileData {
  name?: string;
  phone?: string | null;
  language?: string;
  timezone?: string;
}

/**
 * Hook para buscar perfil do usuário
 */
export const useProfile = (enabled = true) => {
  return useQuery<UserProfile>({
    queryKey: ['user', 'profile'],
    queryFn: () => userService.getProfile(),
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutos - perfil não muda frequentemente
    gcTime: 10 * 60 * 1000, // 10 minutos no garbage collection
    retry: 1,
    refetchOnMount: false, // Não refetch se tiver cache válido
    refetchOnWindowFocus: false, // Não refetch ao focar (perfil não muda externamente)
  });
};

/**
 * Hook para atualizar perfil do usuário
 */
export const useUpdateProfile = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdateProfileData) => userService.updateProfile(data),
    onSuccess: (data) => {
      // Merge na cache existente em vez de substituir. A resposta do PATCH
      // /api/users/me é parcial (não inclui `settings`), por isso um replace
      // descartava os feature flags por conta e colapsava a sidebar V11 para
      // apenas "Channels". Alinha com o padrão do useUploadAvatar.
      queryClient.setQueryData(['user', 'profile'], (old: UserProfile | undefined) =>
        old ? { ...old, ...data } : data
      );
    },
  });
};

/**
 * Hook para upload de avatar
 */
export const useUploadAvatar = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (file: File) => userService.uploadAvatar(file),
    onSuccess: (response) => {
      // Atualiza o avatar no cache
      queryClient.setQueryData(['user', 'profile'], (old: UserProfile | undefined) => {
        if (!old) return old;
        return { ...old, avatarUrl: response.url };
      });
    },
  });
};

/**
 * Hook para mudar password
 */
export const useChangePassword = () => {
  return useMutation({
    mutationFn: ({
      currentPassword,
      newPassword,
      confirmPassword,
    }: {
      currentPassword: string;
      newPassword: string;
      confirmPassword: string;
    }) => userService.changePassword(currentPassword, newPassword, confirmPassword),
  });
};

/**
 * Hook para deletar conta
 */
export const useDeleteAccount = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => userService.deleteAccount(),
    onSuccess: () => {
      // Limpa todos os dados do cache
      queryClient.clear();
    },
  });
};

/**
 * Hook para buscar lista de timezones disponíveis
 */
export const useTimezones = () => {
  return useQuery<Record<string, Array<{value: string, label: string}>>>({
    queryKey: ['timezones'],
    queryFn: () => userService.getTimezones(),
    staleTime: Infinity, // Timezones não mudam, cache permanente
    gcTime: Infinity,
  });
};
